import {
  fork,
  takeEvery,
  put,
  call,
  select,
  takeLatest,
  take,
  race,
  all,
} from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { get } from 'lodash-es'

import getRealtimeWorker from '../../../Live/sagas.realtime'
import { generateActions } from '../../utils/bundleFeature'
import enableAudienceCommentsRealtime from '../CommentsFeature/sagas.realtime'
import { addComment, deleteComment } from '../CommentsFeature/api'
import { showNotice } from '../../../Layout/Notices/Notices'

import { updateLive, fetchLive } from './api'

const mainSidebarPollsNamespace = 'webinar-main-sidebar-polls'
const secondarySidebarPollsNamespace = 'webinar-secondary-sidebar-polls'

const { actionNames: questionsActionNames } = generateActions(
  'webinar-sidebar-questions',
  ['REALTIME_DATA_UPDATE', 'REALTIME_ADD_QUESTION']
)

const { actionNames: chatActionNames } = generateActions(
  'webinar-sidebar-chat',
  [
    'REALTIME_DATA_UPDATE',
    'REALTIME_ADD_CHAT_MESSAGE',
    'FETCH_CHAT_SUCCESS',
    'SET_SHOULD_SCROLL',
  ]
)

const { actionNames: speakerChatActionNames } = generateActions(
  'webinar-sidebar-speaker-chat',
  [
    'REALTIME_DATA_UPDATE',
    'REALTIME_ADD_CHAT_MESSAGE',
    'FETCH_CHAT_SUCCESS',
    'SET_SHOULD_SCROLL',
  ]
)

const { actionNames: handoutsActionNames } = generateActions(
  'webinar-sidebar-handouts',
  ['REALTIME_FILE_UPLOADED', 'UPLOAD_PROGRESS_UPDATED']
)

const { actionNames: pollsMainSidebarActionNames } = generateActions(
  mainSidebarPollsNamespace,
  ['REFETCH']
)

const { actionNames: pollsSecondarySidebarActionNames } = generateActions(
  secondarySidebarPollsNamespace,
  ['REFETCH']
)

function* waitRandomTime(delaySeconds, spreadSeconds) {
  // Wait random time between (delaySeconds) to (delaySeconds+spreadSeconds) seconds, just to not trigger all clients api requests at the exact same time
  // since this would force all clients to miss the cache, rather spread it out a bit
  // (Math.random() * spread) + delay
  const random = Math.random() * spreadSeconds
  const factor = random + delaySeconds
  yield delay(factor * 1000)
}

function* handlePollPublish(isProducer) {
  if (isProducer) {
    return
  }

  yield call(waitRandomTime, 1, 4)
  yield put({ type: pollsMainSidebarActionNames.REFETCH })
}

function* handlePollUpdate(isProducer) {
  if (!isProducer) {
    return
  }

  yield put({ type: pollsMainSidebarActionNames.REFETCH })
  yield put({ type: pollsSecondarySidebarActionNames.REFETCH })
}

function* handlePollAnswer(isProducer, actionNames) {
  if (!isProducer) {
    return
  }

  yield put({ type: actionNames.POLL_ANSWERED })
}

function* debouncePollAnswer(isProducer, actionNames) {
  if (!isProducer) {
    return
  }

  // in new version of redux-saga there is debounce effect
  // to replace the code below when available
  while (true) {
    yield take(actionNames.POLL_ANSWERED)

    while (true) {
      const { debounced } = yield race({
        debounced: delay(1000),
        latestAction: take(actionNames.POLL_ANSWERED),
      })

      if (debounced) {
        yield put({ type: pollsMainSidebarActionNames.REFETCH })
        yield put({ type: pollsSecondarySidebarActionNames.REFETCH })
        break
      }
    }
  }
}

export default ({ actionNames, props, getFeatureRoot }) => {
  const {
    isChatEnabledDefault,
    liveId,
    presenterToken,
    defaultSidebarTab,
    role,
    token,
    uuid,
  } = props

  const isProducer = role === 'producer'

  function* fetchLiveWorker() {
    try {
      yield call(waitRandomTime, 10, 5)
      const { firstlive: liveData } = yield call(fetchLive, liveId, token)
      yield put({ type: actionNames.FETCH_LIVE_DATA_SUCCESS, liveData })
    } catch (err) {
      showNotice({
        message: 'Could not process realtime update for this webinar.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* watchRefetchLiveData() {
    // since fetchLiveWorker introduces some delay before fetching data,
    // we want to cancel the current saga if a new one comes during the wait time
    yield takeLatest(actionNames.REFETCH_LIVE_DATA, fetchLiveWorker)
  }

  function* onRealtimeDataUpdate(action) {
    const { isQuestionsEnabled } = yield select(getFeatureRoot)
    const eventType = get(action, 'realtimeData.event')

    if (eventType === 'poll_publish') {
      yield fork(handlePollPublish, isProducer)
      return
    }

    if (eventType === 'poll_update' || eventType === 'poll_add') {
      yield fork(handlePollUpdate, isProducer)
    }

    if (eventType === 'poll_answer') {
      yield fork(handlePollAnswer, isProducer, actionNames)
      return
    }

    if (eventType === 'live_setting_change') {
      yield put({ type: actionNames.REFETCH_LIVE_DATA })
      return
    }

    if (eventType === 'handout_update') {
      yield put({ type: handoutsActionNames.REALTIME_FILE_UPLOADED })
    }

    // Send the delete event to both namespaces, since we don't know which one the event belongs to
    if (action.realtimeData.event === 'delete') {
      yield all([
        put({
          ...action,
          type: speakerChatActionNames.REALTIME_DATA_UPDATE,
        }),
        put({
          ...action,
          type: chatActionNames.REALTIME_DATA_UPDATE,
        }),
      ])
      return
    }

    if (action.realtimeData.comment_type === 'speaker-chat') {
      yield put({
        ...action,
        type: speakerChatActionNames.REALTIME_DATA_UPDATE,
      })
    } else if (isChatEnabledDefault) {
      yield put({ ...action, type: chatActionNames.REALTIME_DATA_UPDATE })
    }

    if (isQuestionsEnabled && role !== 'audience') {
      yield put({
        ...action,
        type: questionsActionNames.REALTIME_DATA_UPDATE,
      })

      // react on comments processed realtime action by updating the dot on the sidebar
      if (
        eventType === 'add' &&
        action.realtimeData.comment_type === 'question'
      ) {
        yield put({
          type: actionNames.REALTIME_QUESTION_ADDED,
          commentId: action.realtimeData.comment_id,
        })
      }

      if (
        eventType === 'comment_update' &&
        (action.realtimeData.payload.comment_status === 'dismissed' ||
          action.realtimeData.payload.comment_status === 'answered')
      ) {
        yield put({
          type: actionNames.REALTIME_QUESTION_REMOVED,
          commentId: action.realtimeData.payload.comment_id,
        })
      }
    }
  }

  function* watchRealtimeUpdates() {
    // throw comments action when realtime arrives
    yield takeEvery(actionNames.REALTIME_DATA_UPDATE, onRealtimeDataUpdate)
  }

  function* enableRealtime() {
    yield fork(watchRefetchLiveData)
    yield fork(watchRealtimeUpdates)

    try {
      if (role === 'audience') {
        yield enableAudienceCommentsRealtime(
          actionNames.REALTIME_DATA_UPDATE,
          liveId
        )
      } else {
        const realtimeWorker = getRealtimeWorker(
          null,
          actionNames.REALTIME_DATA_UPDATE
        )

        yield call(realtimeWorker, liveId, presenterToken)
      }
    } catch (err) {
      showNotice({
        message:
          'Could not subscribe to realtime updates. Questions and chat functionality will not be available. Refresh the page to reconnect.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* init() {
    yield put({ type: actionNames.INIT, defaultSidebarTab })

    yield fork(enableRealtime)
  }

  function* uploadHandoutsProxy({ payload }) {
    const { actionNames: handoutsFeatureActionNames } = generateActions(
      payload[0],
      ['UPLOAD_HANDOUTS']
    )

    yield put({
      type: handoutsFeatureActionNames.UPLOAD_HANDOUTS,
      payload: payload[1],
    })
  }

  function* watchUploadHandouts() {
    yield takeEvery(actionNames.ON_UPLOAD_HANDOUTS_CLICK, uploadHandoutsProxy)
  }

  function* askQuestionWorker({ values, formSubmitPromise }) {
    const { resolve, reject } = formSubmitPromise
    const { name, content } = values
    const userInfo = { name, uuid }

    try {
      yield call(
        addComment,
        deleteComment,
        liveId,
        token,
        content,
        'question',
        false,
        userInfo
      )
      resolve()
    } catch (err) {
      reject()
    }
  }

  function* watchAskQuestion() {
    yield takeEvery(actionNames.ON_ASK_QUESTION, askQuestionWorker)
  }

  function* toggleQuestionsModeWorker() {
    const { isQuestionsEnabled } = yield select(getFeatureRoot)

    try {
      yield call(updateLive, liveId, {
        question_mode: isQuestionsEnabled ? 'ask' : 'none',
      })
    } catch (err) {
      showNotice({
        message: 'Questions could not be enabled',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* uploadProgressWorker(action) {
    yield put({
      type: handoutsActionNames.UPLOAD_PROGRESS_UPDATED,
      fileName: action.fileName,
      percent: action.percent,
    })
  }

  function* watchToggleQuestionsMode() {
    yield takeEvery(
      actionNames.TOGGLE_QUESTIONS_MODE,
      toggleQuestionsModeWorker
    )
  }

  function* watchUploadProgress() {
    yield takeEvery(actionNames.ON_UPLOAD_PROGRESS, uploadProgressWorker)
  }

  function* onChatInitialLoadWorker() {
    yield delay(200)
    yield put({
      type: chatActionNames.SET_SHOULD_SCROLL,
      shouldScroll: true,
    })
    // This second scroll is just to avoid a weird behaviour with webinar info comment loading in late
    yield delay(800)
    yield put({
      type: chatActionNames.SET_SHOULD_SCROLL,
      shouldScroll: true,
    })
  }

  function* onSpeakerChatInitialLoadWorker() {
    yield put({
      type: chatActionNames.SET_SHOULD_SCROLL,
      shouldScroll: true,
    })
  }

  function* whatOnChatInitialLoad() {
    yield takeEvery(chatActionNames.FETCH_CHAT_SUCCESS, onChatInitialLoadWorker)
  }

  function* whatOnSpeakerChatInitialLoad() {
    yield takeEvery(
      chatActionNames.FETCH_CHAT_SUCCESS,
      onSpeakerChatInitialLoadWorker
    )
  }

  return {
    default: [
      init(),
      watchUploadHandouts(),
      watchAskQuestion(),
      watchToggleQuestionsMode(),
      watchUploadProgress(),
      whatOnChatInitialLoad(),
      whatOnSpeakerChatInitialLoad(),
      debouncePollAnswer(isProducer, actionNames),
    ],
    init,
    watchRealtimeUpdates,
  }
}
